import React from 'react';
import { 
  Building2, Globe, Clock, ExternalLink, Brain, 
  TrendingUp, CheckCircle2 
} from 'lucide-react';

function ResultsDisplay({ results }) {
  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.9) return 'text-green-600';
    if (confidence >= 0.7) return 'text-yellow-600';
    return 'text-orange-600';
  };

  const getConfidenceWidth = (confidence) => {
    return `${confidence * 100}%`;
  };

  const getModelIcon = (model) => {
    const icons = {
      'gpt-4': '🤖',
      'claude-3-opus': '🧠',
      'mistral-7b': '🌟'
    };
    return icons[model] || '🔮';
  };

  return (
    <div className="mt-8 space-y-6">
      {/* Company Info Card */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center">
              <Building2 className="mr-2" size={24} />
              {results.corrected_company ? (
                <span className="flex items-center">
                  <span className="text-gray-500 line-through mr-2">{results.company}</span>
                  <span className="text-green-600">→</span>
                  <span className="ml-2">{results.corrected_company}</span>
                </span>
              ) : (
                results.company
              )}
            </h2>
            {results.corrected_company && (
              <p className="text-sm text-green-600 mt-1 ml-8">
                ✓ Company name corrected
              </p>
            )}
            <p className="text-gray-600 flex items-center mt-1">
              <Globe className="mr-2" size={16} />
              {results.country}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500 flex items-center">
              <Clock className="mr-1" size={14} />
              Total: {results.processing_time_ms}ms
            </p>
            {results.search_time_ms !== null && (
              <p className="text-xs text-gray-400">
                Search: {results.search_time_ms}ms
              </p>
            )}
          </div>
        </div>

        {results.website && (
          <a
            href={results.website}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
          >
            <ExternalLink className="mr-1" size={16} />
            {new URL(results.website).hostname}
          </a>
        )}

        {results.consensus_category && (
          <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
            <p className="text-sm font-medium text-green-800 flex items-center">
              <CheckCircle2 className="mr-2" size={20} />
              Consensus Category: <span className="ml-2 font-bold">{results.consensus_category}</span>
            </p>
          </div>
        )}
      </div>

      {/* Model Results */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <Brain className="mr-2" size={20} />
          AI Model Analysis
        </h3>
        
        <div className="space-y-4">
          {results.results.map((result, index) => (
            <div key={index} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center">
                  <span className="text-2xl mr-2">{getModelIcon(result.model)}</span>
                  <div>
                    <h4 className="font-medium text-gray-800">Model {index + 1}</h4>
                    {result.company_name && (
                      <p className="text-xs text-gray-600 mt-0.5">
                        Identified: <span className="font-medium">{result.company_name}</span>
                      </p>
                    )}
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                      <span className="flex items-center">
                        <Clock className="inline-block mr-1" size={12} />
                        {result.execution_time_ms}ms
                      </span>
                    </div>
                  </div>
                </div>
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                  {result.category}
                </span>
              </div>
              
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-600">Confidence</span>
                  <span className={`text-sm font-medium ${getConfidenceColor(result.confidence)}`}>
                    {(result.confidence * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${
                      result.confidence >= 0.9 ? 'bg-green-500' :
                      result.confidence >= 0.7 ? 'bg-yellow-500' : 'bg-orange-500'
                    }`}
                    style={{ width: getConfidenceWidth(result.confidence) }}
                  />
                </div>
              </div>
              
              <p className="text-sm text-gray-600 italic">
                "{result.reasoning}"
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Search Context */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center">
            <TrendingUp className="mr-2" size={20} />
            Web Search Context
          </h3>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
          <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">
            {results.search_context}
          </pre>
        </div>
      </div>
    </div>
  );
}

export default ResultsDisplay;